import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  stock: number;
  status: string;
  createdAt: string;
}

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    setProducts([
      { id: "PRD-001", name: "หูฟังไร้สาย", category: "อิเล็กทรอนิกส์", price: 2500, stock: 50, status: "active", createdAt: "2026-04-21" },
      { id: "PRD-002", name: "สมาร์ทวอทช์", category: "อิเล็กทรอนิกส์", price: 3200, stock: 30, status: "active", createdAt: "2026-04-20" },
      { id: "PRD-003", name: "เสื้อยืด", category: "เสื้อผ้า", price: 350, stock: 100, status: "active", createdAt: "2026-04-19" },
      { id: "PRD-004", name: "รองเท้ากีฬา", category: "รองเท้า", price: 1200, stock: 25, status: "low_stock", createdAt: "2026-04-18" },
      { id: "PRD-005", name: "กระเป๋าผ้า", category: "กระเป๋า", price: 450, stock: 0, status: "out_of_stock", createdAt: "2026-04-17" },
    ]);
  }, []);

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleStatusChange = (productId: string, newStatus: string) => {
    setProducts(products.map((p) => (p.id === productId ? { ...p, status: newStatus } : p)));
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = { active: "bg-green-100 text-green-800", low_stock: "bg-yellow-100 text-yellow-800", out_of_stock: "bg-red-100 text-red-800", inactive: "bg-gray-100 text-gray-800" };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = { active: "มีสินค้า", low_stock: "สต็อกน้อย", out_of_stock: "หมดสต็อก", inactive: "ไม่ใช้งาน" };
    return labels[status] || status;
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">สินค้า</h1>
          <p className="text-gray-600 mb-6">จัดการและติดตามสินค้าทั้งหมด</p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">สินค้าทั้งหมด</p>
              <p className="text-2xl font-bold text-gray-900">{products.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">มีสินค้า</p>
              <p className="text-2xl font-bold text-green-600">{products.filter((p) => p.status === "active").length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">สต็อกน้อย</p>
              <p className="text-2xl font-bold text-yellow-600">{products.filter((p) => p.status === "low_stock").length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">หมดสต็อก</p>
              <p className="text-2xl font-bold text-red-600">{products.filter((p) => p.status === "out_of_stock").length}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 mb-6 border border-gray-200">
            <div className="flex gap-4">
              <input type="text" placeholder="ค้นหาสินค้า..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg" />
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
                <option value="all">หมวดหมู่ทั้งหมด</option>
                <option value="อิเล็กทรอนิกส์">อิเล็กทรอนิกส์</option>
                <option value="เสื้อผ้า">เสื้อผ้า</option>
                <option value="รองเท้า">รองเท้า</option>
                <option value="กระเป๋า">กระเป๋า</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">รหัสสินค้า</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชื่อสินค้า</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">หมวดหมู่</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ราคา</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สต็อก</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่เพิ่ม</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{product.id}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.category}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">฿{product.price.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.stock}</td>
                    <td className="px-6 py-4">
                      <select value={product.status} onChange={(e) => handleStatusChange(product.id, e.target.value)} className={`px-2 py-1 text-xs rounded-full ${getStatusColor(product.status)}`}>
                        <option value="active">มีสินค้า</option>
                        <option value="low_stock">สต็อกน้อย</option>
                        <option value="out_of_stock">หมดสต็อก</option>
                        <option value="inactive">ไม่ใช้งาน</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
